<template>
    <!-- Labelled landmark so both states stay inside a region rather than
         floating as orphan content, the same shape CookieBanner uses. -->
    <section
        v-if="visible"
        ref="root"
        aria-label="Organizer plan"
        class="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 print:hidden"
    >
        <!-- daisyUI aura: an animated conic-gradient ring. Plain `aura` sweeps
             `currentColor`, so the tone is set here rather than with a style
             modifier — `aura-gold` behind a warning button is gold on gold and
             collapses into a blur. Primary reads against the amber fill and
             against both base-100 values (white / near-black).
             Dropped entirely under prefers-reduced-motion: it animates forever.
             `duration-*` is the sweep speed — one full rotation per period. -->
        <!-- `--aura-radius` is set here rather than left to daisyUI's
             `:has(> .btn)` / `:has(> .card)` detection: both children stay in
             the DOM under v-show (so `aria-controls` always resolves), both
             selectors match at once, and the later `.btn` rule would pin the
             ring to `--radius-field` even while the rounder card is showing.
             `transition-none` matters: `duration-*` sets `transition-duration`
             as well as the `--tw-duration` the sweep reads, and CSS defaults
             `transition-property` to `all` — so without it the radius swap
             above eases between the two values instead of applying at once. -->
        <div
            :class="[
                prefersReducedMotion ? '' : 'aura text-primary duration-[4s] transition-none',
                open ? '[--aura-radius:var(--radius-box)]' : '[--aura-radius:var(--radius-field)]',
            ]"
        >
            <button
                v-show="!open"
                ref="pill"
                type="button"
                class="btn btn-warning shadow-lg"
                :aria-expanded="open"
                aria-controls="plan-upgrade-panel"
                @click="expand"
            >
                <component
                    :is="CONCEPT_ICONS.organizer"
                    :size="16"
                    weight="fill"
                    aria-hidden="true"
                />
                Upgrade
                <PhCaretUp :size="14" aria-hidden="true" />
            </button>

            <div
                v-show="open"
                id="plan-upgrade-panel"
                ref="panel"
                tabindex="-1"
                class="card w-80 max-w-[calc(100vw-2rem)] bg-base-100 text-base-content shadow-lg border border-base-300"
                @keydown.esc="collapse"
            >
                <div class="card-body gap-3 p-4">
                    <div class="flex items-start justify-between gap-2">
                        <div class="flex items-center gap-2 min-w-0">
                            <IconBadge
                                :icon="CONCEPT_ICONS.organizer"
                                :tone="CONCEPT_TONES.organizer"
                                size="sm"
                            />
                            <h2 class="text-base font-bold leading-tight">Organizer plan</h2>
                        </div>
                        <!-- Collapses back to the pill. The prompt itself is
                             always available — there is no dismiss. -->
                        <button
                            type="button"
                            class="btn btn-ghost btn-xs btn-square shrink-0"
                            :aria-expanded="open"
                            aria-controls="plan-upgrade-panel"
                            aria-label="Collapse the Organizer plan prompt"
                            @click="collapse"
                        >
                            <PhX :size="14" aria-hidden="true" />
                        </button>
                    </div>

                    <ul class="flex flex-col gap-1.5 list-none p-0 m-0 text-sm">
                        <li
                            v-for="benefit in BENEFITS"
                            :key="benefit"
                            class="flex items-start gap-2"
                        >
                            <PhCheck
                                :size="14"
                                weight="bold"
                                class="shrink-0 mt-1 text-warning"
                                aria-hidden="true"
                            />
                            <span class="text-base-content">{{ benefit }}</span>
                        </li>
                    </ul>

                    <!-- Pricing lives on the landing domain, so this is a genuine
                         cross-origin link — still built from the Wayfinder helper. -->
                    <a
                        :href="pricing.url()"
                        class="btn btn-warning btn-sm btn-block"
                        @click="trackClick"
                    >
                        View Organizer plan
                        <PhArrowRight :size="14" aria-hidden="true" />
                    </a>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { PhArrowRight, PhCaretUp, PhCheck, PhX } from '@phosphor-icons/vue';
import IconBadge from '@/components/design/IconBadge.vue';
import { CONCEPT_ICONS, CONCEPT_TONES } from '@/design/icons';
import { usePlan } from '@/composables/usePlan';
import { useCookieConsent } from '@/composables/useCookieConsent';
import { useA11yPreferences } from '@/composables/useA11yPreferences';
import { usePostHog } from '@/composables/usePostHog';
import { EVENTS, SOURCES } from '@/posthog/events';
import { pricing } from '@/routes';

// Organizer-only rows from resources/js/data/planFeatures.ts — never a Free
// capability. Keep in step with that registry when tiers change. Note the
// omission of AI disagreement summaries: that registry marks them free:'Light',
// so they are an upgrade, not an unlock.
const BENEFITS = [
    'Jira, Notion & GitHub — import stories, write estimates back',
    'AI story enrichment, retro narratives & your own provider key',
    'Team workspaces with a pooled AI credit balance',
    'Per-session retrospectives',
    'Analytics across every session you organize',
    'Custom card decks you can save and reuse',
];

const page = usePage();
const { isFree } = usePlan();
const { hasChoice } = useCookieConsent();
const { prefersReducedMotion } = useA11yPreferences();
const ph = usePostHog();

/**
 * True while viewing a session whose organizer is on the Organizer plan.
 *
 * Those participants already get the paid experience on someone else's
 * subscription, so selling them one is noise. The flag is page-scoped rather
 * than a shared prop — resolving it globally would cost an organizer lookup on
 * every request — and the two pages that carry it disagree on the key:
 * PlanningRoom nests `session.organizer_is_paid`, SessionJoin sends a
 * top-level `organizerIsPaid`.
 */
function readsAsPaidOrganizerSession(props: unknown): boolean {
    if (typeof props !== 'object' || props === null) {
        return false;
    }
    const pageProps = props as Record<string, unknown>;
    if (pageProps.organizerIsPaid === true) {
        return true;
    }
    const session = pageProps.session;
    if (typeof session !== 'object' || session === null) {
        return false;
    }

    return (session as Record<string, unknown>).organizer_is_paid === true;
}

const inPaidOrganizerSession = computed(() => readsAsPaidOrganizerSession(page.props));

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const pill = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);
let reported = false;

// Permanently available to anyone on the Free plan — nothing dismisses it, the
// panel just collapses to the pill. Two things hold it back: the consent
// banner, which owns the bottom edge until a choice is made, and a session
// already running on the organizer's paid plan.
const visible = computed(() => isFree.value && hasChoice.value && !inPaidOrganizerSession.value);

function expand(): void {
    open.value = true;
}

function collapse(): void {
    if (!open.value) {
        return;
    }
    open.value = false;
    void nextTick(() => pill.value?.focus());
}

function trackClick(): void {
    ph.capture(EVENTS.UPGRADE_PROMPT_CLICKED, { source: SOURCES.UPGRADE_FLOATER });
}

watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }
    void nextTick(() => panel.value?.focus());
    if (!reported) {
        reported = true;
        ph.capture(EVENTS.UPGRADE_PROMPT_SHOWN, { source: SOURCES.UPGRADE_FLOATER });
    }
});

onClickOutside(root, () => {
    open.value = false;
});

onKeyStroke('Escape', () => {
    collapse();
});
</script>
